import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemText,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Build as EquipmentIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { selectUserFacilities } from '../../redux/slices/facilitiesSlice';
import { surgicalEquipmentService } from '../../services/surgicalEquipmentService';
import { useUserData } from '../../hooks/useUserData';
import { DatePicker } from '@mui/x-date-pickers';

const SurgicalEquipmentManagement = () => {
  
  // Use the new useUserData hook for consistent data structure
  const { defaultFacility } = useUserData();
  const userFacilities = useSelector(selectUserFacilities);
  
  // Get default facility ID - prioritize user's defaultFacility, then first userFacility
  const getDefaultFacilityId = useCallback(() => {
    if (defaultFacility?.id && defaultFacility.id !== '') {
      return defaultFacility.id;
    }
    if (userFacilities && userFacilities.length > 0) {
      return userFacilities[0].id;
    }
    return '';
  }, [defaultFacility, userFacilities]);
  
  const defaultFacilityId = getDefaultFacilityId();
  
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [dialogType, setDialogType] = useState('add');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [equipment, setEquipment] = useState([]);

  // Load equipment when facility changes
  const loadEquipment = useCallback(async () => {
    if (!defaultFacilityId) {
      setEquipment([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError('');
    try {
      const equipmentData = await surgicalEquipmentService.getAllEquipment(defaultFacilityId);
      setEquipment(equipmentData);
    } catch (err) {
      setError('Failed to load equipment: ' + err.message);
      console.error('Error loading equipment:', err);
    } finally {
      setLoading(false);
    }
  }, [defaultFacilityId]);

  useEffect(() => {
    loadEquipment();
  }, [loadEquipment]);

  const [formData, setFormData] = useState({
    name: '',
    type: '',
    model: '',
    serialNumber: '',
    location: '',
    manufacturer: '',
    purchaseDate: '',
    warranty: '',
    value: '',
    nextMaintenance: ''
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'operational': return '#4caf50';
      case 'maintenance': return '#ff9800';
      case 'out-of-service': return '#f44336';
      case 'retired': return '#757575';
      default: return '#757575';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'operational': return <CheckCircleIcon />;
      case 'maintenance': return <WarningIcon />;
      case 'out-of-service': return <ErrorIcon />;
      case 'retired': return <SettingsIcon />;
      default: return <ErrorIcon />;
    }
  };

  const handleOpenDialog = (type, equipment = null) => {
    setDialogType(type);
    setSelectedEquipment(equipment);
    if (type === 'add') {
      setFormData({
        name: '',
        type: '',
        model: '',
        serialNumber: '',
        location: '',
        manufacturer: '',
        purchaseDate: '',
        warranty: '',
        value: '',
        nextMaintenance: ''
      });
    } else if (equipment) {
      setFormData({
        name: equipment.name,
        type: equipment.type,
        model: equipment.model,
        serialNumber: equipment.serialNumber,
        location: equipment.location,
        manufacturer: equipment.manufacturer,
        purchaseDate: equipment.purchaseDate,
        warranty: equipment.warranty,
        value: equipment.value,
        nextMaintenance: equipment.nextMaintenance
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedEquipment(null);
  };

  const handleSubmit = async () => {
    if (!defaultFacilityId) {
      setError('No default facility available');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const equipmentData = {
        ...formData,
        facilityId: defaultFacilityId,
        value: parseFloat(formData.value) || 0
      };

      if (dialogType === 'add') {
        await surgicalEquipmentService.createEquipment(equipmentData);
        setSuccess('Equipment added successfully');
        await loadEquipment(); // Reload equipment list
      } else if (dialogType === 'edit' && selectedEquipment) {
        await surgicalEquipmentService.updateEquipment(selectedEquipment.id, equipmentData);
        setSuccess('Equipment updated successfully');
        await loadEquipment(); // Reload equipment list
      }
      handleCloseDialog();
    } catch (err) {
      setError('Failed to save equipment: ' + err.message);
      console.error('Error saving equipment:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEquipment = async (equipmentId) => {
    if (!window.confirm('Are you sure you want to delete this equipment?')) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await surgicalEquipmentService.deleteEquipment(equipmentId);
      setSuccess('Equipment deleted successfully');
      await loadEquipment(); // Reload equipment list
    } catch (err) {
      setError('Failed to delete equipment: ' + err.message);
      console.error('Error deleting equipment:', err);
    } finally {
      setLoading(false);
    }
  };

  const EquipmentCard = ({ item }) => (
    <Card sx={{ 
      height: '100%',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
      }
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: getStatusColor(item.status) }}>
              {getStatusIcon(item.status)}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {item.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {item.type} • {item.model}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={item.status}
            size="small"
            sx={{
              bgcolor: getStatusColor(item.status),
              color: 'white',
              fontWeight: 500,
              textTransform: 'capitalize'
            }}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Location: {item.location}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Serial: {item.serialNumber}
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Maintenance Status
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Typography variant="caption">
              Next: {item.nextMaintenance}
            </Typography>
            <Chip
              label={`$${item.value.toLocaleString()}`}
              size="small"
              variant="outlined"
              color="primary"
            />
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Usage & Warranty
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Typography variant="caption">
              {item.usageHours} hours
            </Typography>
            <Chip
              label={new Date(item.warranty) > new Date() ? 'Under Warranty' : 'Warranty Expired'}
              size="small"
              variant="outlined"
              color={new Date(item.warranty) > new Date() ? 'success' : 'error'}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="View Details">
              <IconButton size="small" onClick={() => handleOpenDialog('view', item)}>
                <ViewIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit Equipment">
              <IconButton size="small" onClick={() => handleOpenDialog('edit', item)}>
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Equipment">
              <IconButton size="small" onClick={() => handleDeleteEquipment(item.id)}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Last maintained: {item.lastMaintenance}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#2c3e50', mb: 1 }}>
            Surgical Equipment Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track equipment status, maintenance, and lifecycle
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('add')}
            sx={{ borderRadius: 2 }}
            disabled={!defaultFacilityId}
          >
            Add Equipment
          </Button>
        </Box>
      </Box>

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#4caf50', mb: 1 }}>
              {equipment.filter(e => e.status === 'operational').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Operational
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#ff9800', mb: 1 }}>
              {equipment.filter(e => e.status === 'maintenance').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Under Maintenance
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#f44336', mb: 1 }}>
              {equipment.filter(e => e.status === 'out-of-service').length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Out of Service
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#9c27b0', mb: 1 }}>
              ${equipment.reduce((acc, item) => acc + item.value, 0).toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Value
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Equipment Cards */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : equipment.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <EquipmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Equipment Found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {defaultFacilityId ? 'No equipment has been added to this facility yet.' : 'No default facility available.'}
          </Typography>
          {defaultFacilityId && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('add')}
            >
              Add First Equipment
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {equipment.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.id}>
              <EquipmentCard item={item} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EquipmentIcon />
            {dialogType === 'add' ? 'Add Equipment' : 
             dialogType === 'edit' ? 'Edit Equipment' : 'Equipment Details'}
          </Box>
          <IconButton onClick={handleCloseDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {dialogType === 'view' && selectedEquipment ? (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ mb: 2 }}>Equipment Information</Typography>
                  <List>
                    <ListItem>
                      <ListItemText primary="Name" secondary={selectedEquipment.name} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Type" secondary={selectedEquipment.type} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Model" secondary={selectedEquipment.model} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Serial Number" secondary={selectedEquipment.serialNumber} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Location" secondary={selectedEquipment.location} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Manufacturer" secondary={selectedEquipment.manufacturer} />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ mb: 2 }}>Financial & Warranty</Typography>
                  <List>
                    <ListItem>
                      <ListItemText primary="Purchase Date" secondary={selectedEquipment.purchaseDate} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Warranty Until" secondary={selectedEquipment.warranty} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Value" secondary={`$${selectedEquipment.value.toLocaleString()}`} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Usage Hours" secondary={selectedEquipment.usageHours} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="Status" secondary={selectedEquipment.status} />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2 }}>Maintenance History</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Technician</TableCell>
                          <TableCell>Cost</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedEquipment.maintenanceHistory.map((maintenance, index) => (
                          <TableRow key={index}>
                            <TableCell>{maintenance.date}</TableCell>
                            <TableCell>{maintenance.type}</TableCell>
                            <TableCell>{maintenance.technician}</TableCell>
                            <TableCell>${maintenance.cost.toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Equipment Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Equipment Type</InputLabel>
                  <Select
                    value={formData.type}
                    label="Equipment Type"
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  >
                    <MenuItem value="Cardiac Equipment">Cardiac Equipment</MenuItem>
                    <MenuItem value="Imaging Equipment">Imaging Equipment</MenuItem>
                    <MenuItem value="Neurosurgery Equipment">Neurosurgery Equipment</MenuItem>
                    <MenuItem value="Anesthesia Equipment">Anesthesia Equipment</MenuItem>
                    <MenuItem value="Ophthalmology Equipment">Ophthalmology Equipment</MenuItem>
                    <MenuItem value="General Surgery Equipment">General Surgery Equipment</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Model"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Serial Number"
                  value={formData.serialNumber}
                  onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Manufacturer"
                  value={formData.manufacturer}
                  onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Purchase Date"
                  value={formData.purchaseDate ? new Date(formData.purchaseDate) : null}
                  onChange={(date) => setFormData({ ...formData, purchaseDate: date ? date.toISOString().split('T')[0] : '' })}
                  slotProps={{
                    textField: { fullWidth: true }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Warranty Until"
                  value={formData.warranty ? new Date(formData.warranty) : null}
                  onChange={(date) => setFormData({ ...formData, warranty: date ? date.toISOString().split('T')[0] : '' })}
                  slotProps={{
                    textField: { fullWidth: true }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Value ($)"
                  type="number"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Next Maintenance"
                  value={formData.nextMaintenance ? new Date(formData.nextMaintenance) : null}
                  onChange={(date) => setFormData({ ...formData, nextMaintenance: date ? date.toISOString().split('T')[0] : '' })}
                  slotProps={{
                    textField: { fullWidth: true }
                  }}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        {dialogType !== 'view' && (
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained">
              {dialogType === 'add' ? 'Add Equipment' : 'Save Changes'}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
};

export default SurgicalEquipmentManagement; 